/**
 * Demo Calendar
 * Visual calendar for managing demo appointments with preparation and follow-up
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Tag,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
  Badge,
} from '@chakra-ui/react';
import {
  MdChevronLeft,
  MdChevronRight,
  MdToday,
  MdEvent,
  MdCheckCircle,
  MdLink,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { getLeads, updateLeadStatus, addLeadActivity } from '../../../services/leadManagement';
import { useAuth } from '../../../contexts/AuthContext';
import { DemoBooking } from '../../../types/availability';

interface DemoEvent {
  id: string;
  leadId?: string; // Optional for custom bookings
  leadName: string;
  companyName: string;
  doorCount: number;
  demoDate: Date;
  status: 'scheduled' | 'completed' | 'cancelled';
  source: 'lead' | 'custom-booking'; // Track source
  contactEmail?: string; // For custom bookings
  contactPhone?: string;
  notes?: string;
  googleCalendarLink?: string; // Link to calendar event
  preparationChecklist: {
    reviewedProfile: boolean;
    confirmedDoorCount: boolean;
    preparedDemoFlow: boolean;
    testedEnvironment: boolean;
  };
  postDemoNotes?: {
    completedAt?: Date;
    feedback?: string;
    nextSteps?: string;
    proposalReady?: boolean;
  };
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  demos: DemoEvent[];
}

export function DemoCalendar() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<'month' | 'week' | 'list'>('month');
  const [demos, setDemos] = useState<DemoEvent[]>([]);
  const [selectedDemo, setSelectedDemo] = useState<DemoEvent | null>(null);
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);

  const {
    isOpen: isPostDemoOpen,
    onOpen: onPostDemoOpen,
    onClose: onPostDemoClose,
  } = useDisclosure();

  // Load demos from both leads and custom booking system
  const loadDemos = async () => {
    try {
      setLoading(true);
      
      // Load demos from leads
      const { leads } = await getLeads();
      const demoLeads = leads.filter(
        (lead) =>
          lead.status === 'demo_scheduled' ||
          lead.status === 'demo_completed'
      );

      const leadDemos: DemoEvent[] = demoLeads
        .filter((lead) => lead.demoScheduledFor)
        .map((lead) => ({
          id: lead.id,
          leadId: lead.id,
          leadName: lead.contactInfo.name,
          companyName: lead.contactInfo.company || 'No company',
          doorCount: lead.businessInfo.doorCount,
          demoDate: lead.demoScheduledFor!.toDate(),
          status:
            lead.status === 'demo_completed'
              ? 'completed'
              : 'scheduled',
          source: 'lead' as const,
          preparationChecklist: {
            reviewedProfile: false,
            confirmedDoorCount: false,
            preparedDemoFlow: false,
            testedEnvironment: false,
          },
          postDemoNotes: lead.demoCompletedAt
            ? {
                completedAt: lead.demoCompletedAt.toDate(),
                feedback: lead.notes || '',
                nextSteps: '',
                proposalReady: false,
              }
            : undefined,
        }));

      // Load demos from custom booking system
      const bookingsRef = collection(db, 'demoBookings');
      const bookingsQuery = query(
        bookingsRef,
        where('status', 'in', ['confirmed', 'completed'])
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      
      const customBookings: DemoEvent[] = bookingsSnapshot.docs.map((doc) => {
        const booking = doc.data() as DemoBooking;
        return {
          id: doc.id,
          leadId: booking.leadId, // May be undefined
          leadName: booking.contactName,
          companyName: booking.companyName || 'No company',
          doorCount: booking.doorCount || 0,
          demoDate: booking.scheduledFor.toDate(),
          status: booking.status === 'completed' ? 'completed' : 'scheduled',
          source: 'custom-booking' as const,
          contactEmail: booking.contactEmail,
          contactPhone: booking.contactPhone,
          notes: booking.notes,
          googleCalendarLink: booking.googleCalendarLink,
          preparationChecklist: {
            reviewedProfile: false,
            confirmedDoorCount: !!booking.doorCount,
            preparedDemoFlow: false,
            testedEnvironment: false,
          },
        };
      });

      // Combine both sources
      const allDemos = [...leadDemos, ...customBookings];
      
      setDemos(allDemos);
      generateCalendar(currentDate, allDemos);
    } catch (error) {
      toast({
        title: 'Error loading demos',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate calendar days
  const generateCalendar = (date: Date, demoEvents: DemoEvent[]) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // Get first day of month
    const firstDay = new Date(year, month, 1);
    
    // Get day of week for first day (0 = Sunday)
    const firstDayOfWeek = firstDay.getDay();
    
    // Calculate start date (may be from previous month)
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDayOfWeek);
    
    // Generate 42 days (6 weeks)
    const days: CalendarDay[] = [];
    const currentDate = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      const dayDemos = demoEvents.filter((demo) => {
        const demoDate = demo.demoDate;
        return (
          demoDate.getDate() === currentDate.getDate() &&
          demoDate.getMonth() === currentDate.getMonth() &&
          demoDate.getFullYear() === currentDate.getFullYear()
        );
      });

      days.push({
        date: new Date(currentDate),
        isCurrentMonth: currentDate.getMonth() === month,
        demos: dayDemos,
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    setCalendarDays(days);
  };

  // Navigation
  const goToPreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
    generateCalendar(newDate, demos);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
    generateCalendar(newDate, demos);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    generateCalendar(today, demos);
  };

  // Handle demo completion
  const handleCompleteDemo = async (demo: DemoEvent) => {
    setSelectedDemo(demo);
    onPostDemoOpen();
  };

  const savePostDemoNotes = async (notes: {
    feedback: string;
    nextSteps: string;
    proposalReady: boolean;
  }) => {
    if (!selectedDemo) return;

    try {
      // Update lead status to demo_completed
      await updateLeadStatus(
        selectedDemo.leadId,
        'demo_completed',
        user?.displayName || 'System',
        `Demo completed. Feedback: ${notes.feedback}. Next steps: ${notes.nextSteps}`
      );

      // Add activity
      await addLeadActivity(
        selectedDemo.leadId,
        'note',
        'Demo completed',
        `Demo completed. Feedback: ${notes.feedback}`,
        user?.displayName || 'System',
        user?.uid || 'system',
        {
          feedback: notes.feedback,
          nextSteps: notes.nextSteps,
          proposalReady: notes.proposalReady,
        }
      );

      toast({
        title: 'Demo marked as completed',
        status: 'success',
        duration: 3000,
      });

      onPostDemoClose();
      loadDemos();
    } catch (error) {
      toast({
        title: 'Error saving notes',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    loadDemos();
  }, []);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  // Get upcoming demos for list view
  const upcomingDemos = demos
    .filter((demo) => demo.status === 'scheduled')
    .sort((a, b) => a.demoDate.getTime() - b.demoDate.getTime());

  const completedDemos = demos
    .filter((demo) => demo.status === 'completed')
    .sort((a, b) => b.demoDate.getTime() - a.demoDate.getTime());

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Demo Calendar</Heading>
        <HStack spacing={4}>
          <Select
            value={view}
            onChange={(e) => setView(e.target.value as 'month' | 'week' | 'list')}
            w="150px"
          >
            <option value="month">Month View</option>
            <option value="list">List View</option>
          </Select>
          <Button
            leftIcon={<Icon as={MdToday} />}
            onClick={goToToday}
            variant="outline"
          >
            Today
          </Button>
        </HStack>
      </Flex>

      {view === 'month' ? (
        <>
          {/* Calendar Navigation */}
          <Card mb={6}>
            <CardBody>
              <Flex justify="space-between" align="center">
                <IconButton
                  aria-label="Previous month"
                  icon={<MdChevronLeft />}
                  onClick={goToPreviousMonth}
                />
                <Heading size="md">{formatDate(currentDate)}</Heading>
                <IconButton
                  aria-label="Next month"
                  icon={<MdChevronRight />}
                  onClick={goToNextMonth}
                />
              </Flex>
            </CardBody>
          </Card>

          {/* Calendar Grid */}
          <Card>
            <CardBody p={0}>
              {/* Day headers */}
              <Grid templateColumns="repeat(7, 1fr)" borderBottomWidth="1px">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <Box
                    key={day}
                    p={3}
                    textAlign="center"
                    fontWeight="bold"
                    fontSize="sm"
                  >
                    {day}
                  </Box>
                ))}
              </Grid>

              {/* Calendar days */}
              <Grid templateColumns="repeat(7, 1fr)">
                {calendarDays.map((day, index) => {
                  const isToday =
                    day.date.toDateString() === new Date().toDateString();
                  
                  return (
                    <Box
                      key={index}
                      borderWidth="1px"
                      borderColor="gray.200"
                      minH="120px"
                      p={2}
                      bg={!day.isCurrentMonth ? 'gray.50' : 'white'}
                      position="relative"
                    >
                      <Text
                        fontSize="sm"
                        fontWeight={isToday ? 'bold' : 'normal'}
                        color={!day.isCurrentMonth ? 'gray.400' : 'gray.700'}
                        mb={1}
                      >
                        {day.date.getDate()}
                        {isToday && (
                          <Badge ml={1} colorScheme="blue" fontSize="xs">
                            Today
                          </Badge>
                        )}
                      </Text>

                      <VStack spacing={1} align="stretch">
                        {day.demos.map((demo) => (
                          <Box
                            key={demo.id}
                            p={1}
                            bg={
                              demo.status === 'completed'
                                ? 'green.50'
                                : demo.status === 'cancelled'
                                ? 'red.50'
                                : demo.source === 'custom-booking'
                                ? 'purple.50'
                                : 'blue.50'
                            }
                            borderRadius="md"
                            fontSize="xs"
                            cursor="pointer"
                            onClick={() => {
                              if (demo.leadId) {
                                navigate(`/admin/sales/leads/${demo.leadId}`);
                              } else {
                                navigate(`/booking/${demo.id}/success`);
                              }
                            }}
                            _hover={{ opacity: 0.8 }}
                          >
                            <HStack spacing={1}>
                              <Text fontWeight="bold" noOfLines={1} flex={1}>
                                {formatTime(demo.demoDate)}
                              </Text>
                              {demo.source === 'custom-booking' && (
                                <Icon as={MdLink} boxSize={3} color="purple.600" />
                              )}
                            </HStack>
                            <Text noOfLines={1}>{demo.leadName}</Text>
                            <Text noOfLines={1} color="gray.600">
                              {demo.companyName}
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  );
                })}
              </Grid>
            </CardBody>
          </Card>
        </>
      ) : (
        // List View
        <Grid templateColumns="repeat(2, 1fr)" gap={6}>
          {/* Upcoming Demos */}
          <Card>
            <CardHeader>
              <Heading size="md">Upcoming Demos ({upcomingDemos.length})</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {upcomingDemos.length === 0 ? (
                  <Text color="gray.500">No upcoming demos scheduled</Text>
                ) : (
                  upcomingDemos.map((demo) => (
                    <Card key={demo.id} variant="outline">
                      <CardBody>
                        <VStack spacing={3} align="stretch">
                          <Flex justify="space-between" align="start">
                            <Box flex={1}>
                              <HStack>
                                <Text fontWeight="bold" fontSize="lg">
                                  {demo.leadName}
                                </Text>
                                {demo.source === 'custom-booking' && (
                                  <Badge colorScheme="purple" fontSize="xs">
                                    Web Booking
                                  </Badge>
                                )}
                              </HStack>
                              <Text color="gray.600">{demo.companyName}</Text>
                              <Text fontSize="sm" color="gray.500">
                                {demo.doorCount} doors
                              </Text>
                              {demo.contactEmail && (
                                <Text fontSize="sm" color="gray.500">
                                  {demo.contactEmail}
                                </Text>
                              )}
                              {demo.contactPhone && (
                                <Text fontSize="sm" color="gray.500">
                                  {demo.contactPhone}
                                </Text>
                              )}
                            </Box>
                            <VStack spacing={2} align="end">
                              <Tag colorScheme="blue">Scheduled</Tag>
                              <Text fontSize="sm" fontWeight="bold">
                                {demo.demoDate.toLocaleDateString()}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {formatTime(demo.demoDate)}
                              </Text>
                              {demo.googleCalendarLink && (
                                <Button
                                  as="a"
                                  href={demo.googleCalendarLink}
                                  target="_blank"
                                  size="xs"
                                  leftIcon={<Icon as={MdEvent} />}
                                  variant="outline"
                                >
                                  Calendar
                                </Button>
                              )}
                            </VStack>
                          </Flex>

                          <Divider />

                          <Box>
                            <Text fontSize="sm" fontWeight="bold" mb={2}>
                              Preparation Checklist:
                            </Text>
                            <VStack spacing={1} align="stretch">
                              <Checkbox size="sm" isChecked={demo.preparationChecklist.reviewedProfile}>
                                Review lead profile
                              </Checkbox>
                              <Checkbox size="sm" isChecked={demo.preparationChecklist.confirmedDoorCount}>
                                Confirm door count & pain points
                              </Checkbox>
                              <Checkbox size="sm" isChecked={demo.preparationChecklist.preparedDemoFlow}>
                                Prepare custom demo flow
                              </Checkbox>
                              <Checkbox size="sm" isChecked={demo.preparationChecklist.testedEnvironment}>
                                Test demo environment
                              </Checkbox>
                            </VStack>
                          </Box>

                          {demo.notes && (
                            <Box>
                              <Text fontSize="sm" fontWeight="bold" mb={1}>
                                Notes:
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {demo.notes}
                              </Text>
                            </Box>
                          )}

                          <HStack spacing={2}>
                            {demo.leadId ? (
                              <Button
                                size="sm"
                                leftIcon={<Icon as={MdEvent} />}
                                onClick={() => navigate(`/admin/sales/leads/${demo.leadId}`)}
                                flex={1}
                              >
                                View Lead
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                leftIcon={<Icon as={MdLink} />}
                                onClick={() => navigate(`/booking/${demo.id}/success`)}
                                flex={1}
                              >
                                View Booking
                              </Button>
                            )}
                            <Button
                              size="sm"
                              colorScheme="green"
                              leftIcon={<Icon as={MdCheckCircle} />}
                              onClick={() => handleCompleteDemo(demo)}
                              flex={1}
                            >
                              Complete Demo
                            </Button>
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  ))
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Completed Demos */}
          <Card>
            <CardHeader>
              <Heading size="md">Recent Completions ({completedDemos.length})</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {completedDemos.length === 0 ? (
                  <Text color="gray.500">No completed demos</Text>
                ) : (
                  completedDemos.slice(0, 10).map((demo) => (
                    <Card key={demo.id} variant="outline">
                      <CardBody>
                        <VStack spacing={3} align="stretch">
                          <Flex justify="space-between" align="start">
                            <Box flex={1}>
                              <Text fontWeight="bold" fontSize="lg">
                                {demo.leadName}
                              </Text>
                              <Text color="gray.600">{demo.companyName}</Text>
                              <Text fontSize="sm" color="gray.500">
                                {demo.doorCount} doors
                              </Text>
                            </Box>
                            <VStack spacing={2} align="end">
                              <Tag colorScheme="green">Completed</Tag>
                              <Text fontSize="sm">
                                {demo.demoDate.toLocaleDateString()}
                              </Text>
                            </VStack>
                          </Flex>

                          {demo.postDemoNotes && (
                            <>
                              <Divider />
                              <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={1}>
                                  Feedback:
                                </Text>
                                <Text fontSize="sm" color="gray.600">
                                  {demo.postDemoNotes.feedback}
                                </Text>
                              </Box>
                              {demo.postDemoNotes.nextSteps && (
                                <Box>
                                  <Text fontSize="sm" fontWeight="bold" mb={1}>
                                    Next Steps:
                                  </Text>
                                  <Text fontSize="sm" color="gray.600">
                                    {demo.postDemoNotes.nextSteps}
                                  </Text>
                                </Box>
                              )}
                            </>
                          )}

                          <Button
                            size="sm"
                            leftIcon={<Icon as={MdEvent} />}
                            onClick={() => navigate(`/admin/sales/leads/${demo.leadId}`)}
                          >
                            View Lead
                          </Button>
                        </VStack>
                      </CardBody>
                    </Card>
                  ))
                )}
              </VStack>
            </CardBody>
          </Card>
        </Grid>
      )}

      {/* Post-Demo Notes Modal */}
      <Modal isOpen={isPostDemoOpen} onClose={onPostDemoClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Complete Demo - {selectedDemo?.leadName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Demo Feedback</FormLabel>
                <Textarea
                  id="feedback"
                  placeholder="How did the demo go? Key points discussed, client reactions, etc."
                  rows={4}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Next Steps</FormLabel>
                <Textarea
                  id="nextSteps"
                  placeholder="What are the next steps? When to follow up?"
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <Checkbox id="proposalReady">
                  Client is ready for proposal
                </Checkbox>
              </FormControl>

              {selectedDemo && (
                <Box p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    Lead Information:
                  </Text>
                  <Text fontSize="sm">
                    <strong>Company:</strong> {selectedDemo.companyName}
                  </Text>
                  <Text fontSize="sm">
                    <strong>Door Count:</strong> {selectedDemo.doorCount}
                  </Text>
                  <Text fontSize="sm">
                    <strong>Demo Date:</strong>{' '}
                    {selectedDemo.demoDate.toLocaleString()}
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPostDemoClose}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={() => {
                const feedback = (
                  document.getElementById('feedback') as HTMLTextAreaElement
                )?.value;
                const nextSteps = (
                  document.getElementById('nextSteps') as HTMLTextAreaElement
                )?.value;
                const proposalReady = (
                  document.getElementById('proposalReady') as HTMLInputElement
                )?.checked;

                if (feedback) {
                  savePostDemoNotes({ feedback, nextSteps, proposalReady });
                } else {
                  toast({
                    title: 'Feedback is required',
                    status: 'warning',
                    duration: 3000,
                  });
                }
              }}
            >
              Save & Complete Demo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
